import cx from 'classnames';
import { ReactNode } from 'react';

import MessengerPreviewAvatar1 from 'assets/images/MessengerPreviewAvatar1.jpg';
import MessengerPreviewAvatar2 from 'assets/images/MessengerPreviewAvatar2.jpg';
import MessengerPreviewAvatar3 from 'assets/images/MessengerPreviewAvatar3.jpg';

/*
 * All preview components render inside the 176×128px inner frame defined in
 * LayoutCard.tsx. Positions here are in the coordinate space of that frame
 * (origin = top-left). Sidebar strip occupies x=18..38 (20px wide). Content
 * is positioned with a 4px breathing gap from the sidebar (x ≥ 42).
 *
 * Canonical Figma references:
 *   Video (1955:162969) · Messenger (1955:163074) · Calendar (1955:163098)
 */

// ─── Inline icons (currentColor so the parent can tint them) ────────────────────

const IconVideo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 10 10" fill="none" className={className} aria-hidden="true">
    <path
      d="M6.25 4.16659L8.14708 3.21826C8.21059 3.18653 8.28116 3.17154 8.35208 3.17474C8.42301 3.17793 8.49194 3.1992 8.55234 3.23651C8.61273 3.27383 8.6626 3.32596 8.69719 3.38796C8.73178 3.44996 8.74996 3.51976 8.75 3.59076V6.40909C8.74996 6.48009 8.73178 6.5499 8.69719 6.6119C8.6626 6.6739 8.61273 6.72603 8.55234 6.76334C8.49194 6.80066 8.42301 6.82192 8.35208 6.82512C8.28116 6.82831 8.21059 6.81333 8.14708 6.78159L6.25 5.83326V4.16659Z"
      stroke="currentColor"
      strokeWidth="0.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.41667 2.5H2.08333C1.6231 2.5 1.25 2.8731 1.25 3.33333V6.66667C1.25 7.1269 1.6231 7.5 2.08333 7.5H5.41667C5.8769 7.5 6.25 7.1269 6.25 6.66667V3.33333C6.25 2.8731 5.8769 2.5 5.41667 2.5Z"
      stroke="currentColor"
      strokeWidth="0.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconChat = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 10 10" fill="none" className={className} aria-hidden="true">
    <path
      d="M8.75002 5.83333L7.50002 4.58333H4.58335C4.47285 4.58333 4.36687 4.53943 4.28873 4.46129C4.21059 4.38315 4.16669 4.27717 4.16669 4.16667V1.66667C4.16669 1.55616 4.21059 1.45018 4.28873 1.37204C4.36687 1.2939 4.47285 1.25 4.58335 1.25H8.33335C8.44386 1.25 8.54984 1.2939 8.62798 1.37204C8.70612 1.45018 8.75002 1.55616 8.75002 1.66667V5.83333Z"
      stroke="currentColor"
      strokeWidth="0.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.83333 6.24984V7.08317C5.83333 7.19368 5.78943 7.29966 5.71129 7.3778C5.63315 7.45594 5.52717 7.49984 5.41667 7.49984H2.5L1.25 8.74984V4.58317C1.25 4.47266 1.2939 4.36668 1.37204 4.28854C1.45018 4.2104 1.55616 4.1665 1.66667 4.1665H2.5"
      stroke="currentColor"
      strokeWidth="0.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCalendar = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 10 10" fill="none" className={className} aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.66699 0.875C6.87395 0.875176 7.04199 1.043 7.04199 1.25V1.70801H7.5C8.16698 1.70818 8.70863 2.24908 8.70898 2.91602V7.91602C8.70898 8.58325 8.16719 9.12482 7.5 9.125H2.5C1.83281 9.12482 1.29199 8.58325 1.29199 7.91602V2.91602C1.29234 2.24908 1.83302 1.70818 2.5 1.70801H2.95801V1.25C2.95801 1.043 3.12605 0.875176 3.33301 0.875C3.54011 0.875 3.70801 1.04289 3.70801 1.25V1.70801H6.29199V1.25C6.29199 1.04289 6.45989 0.875 6.66699 0.875ZM2.04199 4.95801V7.91602C2.04199 8.16904 2.24702 8.37482 2.5 8.375H7.5C7.75298 8.37482 7.95898 8.16904 7.95898 7.91602V4.95801H2.04199ZM2.5 2.45801C2.24724 2.45818 2.04234 2.66329 2.04199 2.91602V4.20801H7.95898V2.91602C7.95863 2.66329 7.75277 2.45818 7.5 2.45801H7.04199V2.91699C7.04182 3.12384 6.87384 3.29182 6.66699 3.29199C6.45999 3.29199 6.29217 3.12395 6.29199 2.91699V2.45801H3.70801V2.91699C3.70783 3.12395 3.54001 3.29199 3.33301 3.29199C3.12616 3.29182 2.95818 3.12384 2.95801 2.91699V2.45801H2.5Z"
      fill="currentColor"
    />
  </svg>
);

// Small clock icon for the upcoming-call time buttons inside the Video preview.
const IconClock = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 10 10" fill="none" className={className} aria-hidden="true">
    <circle cx="5" cy="5" r="3.25" stroke="currentColor" strokeWidth="0.75" />
    <path d="M5 3.25 V5 H6.25" stroke="currentColor" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Sidebar strip ─────────────────────────────────────────────────────────────
// 20×60 white rail at left-[18px] bottom-[34px]. Three icon slots top→bottom:
// the top slot is always the active section (teal background, white icon).

interface SidebarStripProps {
  topIcon: ReactNode;
  middleIcon: ReactNode;
  bottomIcon: ReactNode;
}

const SidebarStrip = ({ topIcon, middleIcon, bottomIcon }: SidebarStripProps) => (
  <div className="absolute bottom-[34px] left-[18px] h-[60px] w-5 overflow-hidden rounded-[6px] bg-white shadow-[0px_8px_16px_-4px_rgba(87,97,117,0.16)]">
    <div className="absolute bottom-[42px] left-1/2 flex h-4 w-4 -translate-x-1/2 items-center justify-center rounded-[4px] bg-primaryComp-50 text-white">
      <span className="block h-2.5 w-2.5">{topIcon}</span>
    </div>
    <div className="absolute left-1/2 top-1/2 flex h-4 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center text-gray-50">
      <span className="block h-2.5 w-2.5">{middleIcon}</span>
    </div>
    <div className="absolute bottom-[2px] left-1/2 flex h-4 w-4 -translate-x-1/2 items-center justify-center text-gray-50">
      <span className="block h-2.5 w-2.5">{bottomIcon}</span>
    </div>
  </div>
);

// ─── Reusable inner card for the Video preview list ────────────────────────────

interface CallCardProps {
  active?: boolean;
  trailing: ReactNode;
}

const CallCard = ({ active = false, trailing }: CallCardProps) => (
  <div
    className={cx(
      'flex items-center gap-[10px] overflow-hidden rounded-[4px] p-[8px]',
      active ? 'border-[0.5px] border-primaryComp-50 bg-primaryComp-10' : 'bg-white'
    )}
  >
    <div className="flex flex-1 flex-col gap-[3px]">
      <div className="h-1 w-[45px] rounded-[3px] bg-black10" />
      <div className="h-1 w-5 rounded-[3px] bg-black10" />
    </div>
    {trailing}
  </div>
);

const ParticipantsCard = () => (
  <div className="overflow-hidden rounded-[4px] bg-white p-[8px]">
    <div className="h-1 w-[65px] rounded-[3px] bg-black10" />
    <div className="mt-1 flex items-center gap-0.5">
      <div className="flex isolate items-center">
        <div className="-mr-[0.667px] z-[3] h-[6.667px] w-[6.667px] rounded-[2.667px] border-[0.667px] border-white bg-primaryComp-50" />
        <div className="-mr-[0.667px] z-[2] h-[6.667px] w-[6.667px] rounded-[2.667px] border-[0.667px] border-white bg-[#f58634]" />
        <div className="z-[1] h-[6.667px] w-[6.667px] rounded-[2.667px] border-[0.667px] border-white bg-[#b353c6]" />
      </div>
      <div className="h-1 w-[14px] rounded-[3px] bg-black10" />
    </div>
  </div>
);

// ─── Video calls preview (Figma node 1955:162969) ──────────────────────────────
// 5 stacked cards: active call, two upcoming time-buttons, two participant cards.
// The 5th card sits behind the bottom fade and is mostly hidden, matching Figma.

export const VideoCallsPreview = () => (
  <>
    <SidebarStrip
      topIcon={<IconVideo className="h-full w-full" />}
      middleIcon={<IconChat className="h-full w-full" />}
      bottomIcon={<IconCalendar className="h-full w-full" />}
    />

    <span className="absolute left-[42px] top-[6px] whitespace-nowrap text-[8px] font-medium leading-[1.5] text-gray-80">
      Scheduled calls
    </span>

    <div className="absolute left-[42px] top-[22px] flex w-[128px] flex-col gap-[4px]">
      {/* Card 1 — active call with teal Join button + video icon */}
      <CallCard
        active
        trailing={
          <div className="flex shrink-0 items-center justify-center gap-[3.2px] rounded-[4.8px] bg-primary-50 px-[6.4px] py-1 text-white">
            <IconVideo className="h-2 w-2 stroke-current" />
            <span className="text-[5.6px] font-medium leading-[8px]">Join</span>
          </div>
        }
      />

      {/* Card 2 — upcoming 13:00 with clock icon */}
      <CallCard
        trailing={
          <div className="flex w-9 shrink-0 items-center justify-center gap-[3.2px] rounded-[4.8px] bg-gray-10 px-[6.4px] py-1 text-gray-60">
            <IconClock className="h-2 w-2 stroke-current" />
            <span className="text-[5.6px] font-medium leading-[8px]">13:00</span>
          </div>
        }
      />

      {/* Card 3 — upcoming 15:00 with clock icon */}
      <CallCard
        trailing={
          <div className="flex w-9 shrink-0 items-center justify-center gap-[3.2px] rounded-[4.8px] bg-gray-10 px-[6.4px] py-1 text-gray-60">
            <IconClock className="h-2 w-2 stroke-current" />
            <span className="text-[5.6px] font-medium leading-[8px]">15:00</span>
          </div>
        }
      />

      {/* Card 4 & 5 — participant cards (Card 5 sits behind the bottom fade) */}
      <ParticipantsCard />
      <ParticipantsCard />
    </div>
  </>
);

// ─── Messenger preview (Figma node 1955:163074) ────────────────────────────────
// Three message bubbles + avatars (positions shifted +4px right of the prior
// Figma JSON to leave a visible breathing gap from the sidebar at x=38).

export const MessengerPreview = () => (
  <>
    <SidebarStrip
      topIcon={<IconChat className="h-full w-full" />}
      middleIcon={<IconVideo className="h-full w-full" />}
      bottomIcon={<IconCalendar className="h-full w-full" />}
    />

    <span className="absolute left-[43px] top-[6px] whitespace-nowrap text-[8px] font-medium leading-[1.5] text-gray-80">
      Team chat
    </span>

    {/* Incoming message 1 (top) */}
    <div className="absolute left-[43px] top-[28px] h-[14px] w-[14px] overflow-hidden rounded-[4px]">
      <img src={MessengerPreviewAvatar1} alt="" aria-hidden="true" className="h-full w-full object-cover" />
    </div>
    <div className="absolute left-[61px] top-[32px] h-6 w-20 rounded-bl-[4px] rounded-br-[4px] rounded-tr-[4px] bg-black10" />

    {/* Outgoing message (middle, teal bubble) */}
    <div className="absolute left-[156px] top-[62px] h-[14px] w-[14px] overflow-hidden rounded-[4px]">
      <img src={MessengerPreviewAvatar3} alt="" aria-hidden="true" className="h-full w-full object-cover" />
    </div>
    <div className="absolute left-[72px] top-[66px] h-6 w-20 rounded-bl-[4px] rounded-br-[4px] rounded-tl-[4px] bg-primaryComp-20" />

    {/* Incoming message 2 (bottom) */}
    <div className="absolute left-[43px] top-[96px] h-[14px] w-[14px] overflow-hidden rounded-[4px]">
      <img src={MessengerPreviewAvatar2} alt="" aria-hidden="true" className="h-full w-full object-cover" />
    </div>
    <div className="absolute left-[61px] top-[100px] h-6 w-20 rounded-bl-[4px] rounded-br-[4px] rounded-tr-[4px] bg-black10" />
  </>
);

// ─── Calendar preview (Figma node 1955:163098) ─────────────────────────────────
// 7×5 grid of 15×15 cells (4px column gap, 6px row gap). Shifted +4px right
// (left-[40px] instead of left-[36px]) so the grid no longer overlaps the
// sidebar's 38px right edge — gives a clear visible gap.

const CALENDAR_HIGHLIGHTED_INDEX = 17;
const CALENDAR_OUTLINED_INDICES = new Set([5, 6, 12, 13, 19, 20, 26, 27, 33, 34]);

export const CalendarPreview = () => (
  <>
    <SidebarStrip
      topIcon={<IconCalendar className="h-full w-full" />}
      middleIcon={<IconVideo className="h-full w-full" />}
      bottomIcon={<IconChat className="h-full w-full" />}
    />

    <span className="absolute left-[48px] top-[6px] whitespace-nowrap text-[8px] font-medium leading-[1.5] text-gray-80">
      Month
    </span>

    <div className="absolute left-[42px] top-[24px] grid grid-cols-7 gap-x-1 gap-y-[6px]">
      {Array.from({ length: 35 }, (_, i) => (
        <div
          key={i}
          className={cx(
            'h-[15px] w-[15px] rounded-[4px]',
            i === CALENDAR_HIGHLIGHTED_INDEX
              ? 'bg-primaryComp-50'
              : CALENDAR_OUTLINED_INDICES.has(i)
              ? 'border-[0.5px] border-gray-40'
              : 'bg-gray-30'
          )}
        />
      ))}
    </div>
  </>
);
